<template>
  <v-container>
    <v-row class="ga-6">
      <v-col>
        <v-card elevation="0" class="pl-4">
          <v-form ref="form" lazy-validation>
            <v-card-title v-show="!loading" class="text-h5 primary text-white">
              Create Distributor
            </v-card-title>

            <v-progress-linear
              :active="distributorPending"
              :indeterminate="true"
              class="ma-0"
              height="4"
              style="top: -2px"
            >
              Loading...
            </v-progress-linear>

            <v-card-text v-if="!distributorPending">
              <distributor-info-form
                :distributor="distributor"
                v-model:nexusStates="nexusStates"
                :nexusStatesLoading="nexusStatesLoading"
                :showOwnerFields="true"
                v-model:ownerFirstName="ownerFirstName"
                v-model:ownerLastName="ownerLastName"
                v-model:ownerEmail="ownerEmail"
                @address-changed="addressChanged = true"
              />
            </v-card-text>

            <v-card-actions class="d-flex justify-end">
              <v-spacer />
              <v-btn
                class="flex-grow-0"
                @click="$router.push('/admin/distributors')"
                :disabled="distributorPending"
                data-testid="distributor-add-cancel-btn"
                variant="flat"
              >
                Cancel
              </v-btn>
              <v-btn
                v-if="hasAccess"
                @click="updateCreateAddUsersButtonPress"
                color="primary"
                class="flex-grow-0"
                :disabled="distributorPending || loading"
                variant="outlined"
              >
                Create and add users
              </v-btn>
              <v-btn
                @click="updateCreateButtonPress"
                v-if="hasAccess"
                data-testid="distributor-add-create-btn"
                color="primary"
                class="flex-grow-0"
                :disabled="distributorPending || loading"
                variant="flat"
              >
                Create
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
      <validate-address-dialog
        ref="validateAddressDialog"
        :originalLocation="distributor.address"
        @useValidatedAddress="updateAddressToValidated"
        @useOriginalAddress="useOriginalAddress"
      />
    </v-row>
  </v-container>
</template>

<script>
// PR-8 (frontend-rearchitecture, role-sweep-tier-1):
// Permission gates migrated to capability-based `can('...')` via
// usePermissions. This page is create-only and SuperAdmin-only (routed
// solely at /admin/distributors/add behind requireCapability(
// "superadmin.tenants")), so it never loads an existing distributor.
import { canonicalNexusNames } from "@/data/usStates";
import {
  isUnparseableAddress,
  unparseableAddressMessage,
} from "@/utils/addressParse";
import Distributor from "@/models/Distributor";
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
import ValidateAddressDialog from "@/pages/distributor/store-editor/StoreLocations/ValidateAddressDialog.vue";
import DistributorInfoForm from "./DistributorInfoForm.vue";
import { usePermissions } from "@/composables/usePermissions";
import { emitSidebarRefresh } from "@/stores/sidebarRefresh";

export default {
  setup() {
    const { can } = usePermissions();
    return { can };
  },

  components: {
    "validate-address-dialog": ValidateAddressDialog,
    "distributor-info-form": DistributorInfoForm,
  },
  data() {
    return {
      distributor: Distributor.generateEmpty(),

      distributorPending: true,

      nexusStates: [],
      nexusStatesLoading: false,

      // Owner onboarding fields (create only). SA "Create Distributor"
      // onboards the tenant Owner as part of create. Card:
      // https://trello.com/c/fRuMEVvw.
      ownerFirstName: "",
      ownerLastName: "",
      ownerEmail: "",

      addressChanged: false,
      pendingSubmissionCallback: null,

      loading: false,
    };
  },

  computed: {
    hasAccess() {
      // Create a distributor: SA or DM (both hold distributor.manage).
      return this.can("distributor.manage");
    },

  },

  mixins: [rules, helpers],

  mounted() {
    this.$store.state.title = "Account & Profile";
    this.$store.state.topTitle = false;

    // Create-only page: there is no existing distributor to load.
    this.distributorPending = false;
  },

  methods: {
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async loadHomeState() {
      this.nexusStatesLoading = true;
      let location = JSON.parse(JSON.stringify(this.distributor.address));
      try {
        let response = await this.$axios.post(
          "/address_validation/parseAndValidateAddress",
          {
            location: location,
          }
        );
        if (isUnparseableAddress(response.data)) {
          // Keep whatever nexus states the user already chose.
          this.$store.dispatch("raiseError", unparseableAddressMessage(response.data));
        } else if (response.data.error) {
          this.$store.dispatch("raiseError", response.data.error);
          this.$refs["validateAddressDialog"].showErrorDialog();
        } else {
          this.nexusStates = canonicalNexusNames([response.data.region]);
        }
      } catch (err) {
        this.$store.dispatch('raiseError', err);
        this.nexusStates = [];
      } finally {
        this.nexusStatesLoading = false;
      }
    },

    updateCreateButtonPress() {
      if (this.addressChanged) {
        this.pendingSubmissionCallback = this.redirectToHome;
        this.openValidateAddressDialog();
      } else {
        this.submitHandler(this.redirectToHome);
      }
    },

    updateCreateAddUsersButtonPress() {
      if (this.addressChanged) {
        this.pendingSubmissionCallback = this.redirectToAddAccounts;
        this.openValidateAddressDialog();
      } else {
        this.submitHandler(this.redirectToAddAccounts);
      }
    },

    openValidateAddressDialog() {
      this.$refs["validateAddressDialog"].show();
    },

    updateAddressToValidated(location) {
      this.distributor.address =
        location.street_and_number +
        ", " +
        (location.suite_unit_etc ? location.suite_unit_etc + ", " : "") +
        location.city +
        ", " +
        location.region +
        ", " +
        location.zip_code +
        ", " +
        location.country;

      // Full NAME, never the 2-letter code the validator returns: the tax
      // engine compares names, and a code beside the grid's name rendered
      // as a duplicate "WA" + "Washington" (card #237).
      this.nexusStates = canonicalNexusNames([
        ...this.nexusStates,
        location.region,
      ]);

      this.addressChanged = false;

      this.submitHandler(this.pendingSubmissionCallback || this.redirectToHome);
    },

    useOriginalAddress() {
      // Address validation must NEVER block distributor creation (card
      // AM460gOY). ValidateAddressDialog emits this either from the user
      // clicking "Use Original" (dialog was shown) OR when validation is
      // unavailable / fails and it auto-saves via the _fallback path (dialog
      // was NOT shown). Both cases must proceed to submitHandler. A prior
      // `if (!this.dialogShownToUser) return;` guard here silently dropped the
      // auto-save emit -- so on any fallback the create never fired, no
      // distributor was created, and no invite email sent. Siblings
      // (DistributorAccountInfo, VendorAddEdit) wire useOriginalAddress
      // straight to submitHandler with no guard, which is the correct
      // non-blocking behavior.
      this.addressChanged = false;
      this.submitHandler(this.pendingSubmissionCallback || this.redirectToHome);
    },

    // Success-side finalization after a distributor create/update + nexus
    // save. Previously this lived inside updateProductTypes(); the product-
    // type binding was removed (clean break) but the save flow still owns
    // the success toast, loading reset, navigation, and the caller callback.
    finalizeSave(actionVerb, callback) {
      this.$store.dispatch(
        "raiseSuccess",
        `Distributor ${this.distributor._jv.id} ${actionVerb}`
      );
      this.loading = false;
      typeof callback === "function" && callback();
    },

    async saveNexusStates(distributorID) {
      let data = this.nexusStates;
      let response;
      try {
        response = await this.$axios.post(
          `/distributors/${distributorID}/edit_nexus_states`,
          {
            meta: {
              method: "edit_nexus_states",
              args: {
                nexus_states: data,
              },
            },
          }
        );
      } catch (err) {
        this.$store.dispatch("raiseError", err);
      } finally {
        this.$store.dispatch("raiseSuccess", "Nexus States Updated");
      }
    },

    async submitHandler(callback) {
      const isValid = this.$refs.form.validate();

      if (isValid) {
        this.loading = true;
        try {
          // Use _jv.id to detect update vs create: _jv.id is set once the
          // create below has resolved (the page never loads an existing
          // distributor), so a repeat submit patches instead of re-creating.
          if (this.distributor._jv && this.distributor._jv.id) {
            let distributor = await this.$store.dispatch(
              "jv/patch",
              this.distributor
            );

            this.distributor = distributor;
            await this.saveNexusStates(this.distributor._jv.id);
            this.finalizeSave("updated", callback);
          } else {
            // CREATE now goes through the SA create-with-owner endpoint
            // (POST /api/superadmin/distributors), NOT the bare SAFRS
            // POST /distributors. That endpoint provisions the tenant
            // Owner as part of create: it creates the Distributor row, an
            // inactive Owner account, a set-password invite token, a
            // pinned subscription, and queues the owner invite email --
            // all atomically. Card https://trello.com/c/fRuMEVvw.
            //
            // The payload is a plain JSON body (not a JSON:API document),
            // so the empty-relationship-collection problem that the SAFRS
            // path had (accounts/campaigns/products: [] rejected by
            // _add_rels) cannot occur here -- we send only the named
            // scalar fields, never the distributor's relationship arrays.
            // Address validation stays NON-BLOCKING: this runs only after
            // the address-validation dialog has resolved (see
            // updateCreateButtonPress / useOriginalAddress).
            const response = await this.$axios.post(
              "/superadmin/distributors",
              {
                company_name: this.distributor.name,
                owner_first_name: this.ownerFirstName,
                owner_last_name: this.ownerLastName,
                owner_email: this.ownerEmail,
                email: this.distributor.email,
                address: this.distributor.address,
                nexus_states: this.nexusStates,
              }
            );

            // Normalize the created distributor so downstream navigation
            // (redirectToAddAccounts uses _jv.id) and finalizeSave keep
            // working with the same shape they used for the SAFRS path.
            const created =
              (response && response.data && response.data.distributor) || {};
            this.distributor = {
              ...this.distributor,
              name: created.name || this.distributor.name,
              _jv: { id: created.id, type: "Distributor" },
            };
            // A new distributor moves the SA sidebar DISTRIBUTORS count ->
            // refresh it. Emit only on this CREATE branch (the jv/patch update
            // branch above does not change the count) and only after the POST
            // resolved (a failed create throws to the catch and never reaches
            // here). Generic delegated seam (https://trello.com/c/g9K52FsA).
            emitSidebarRefresh("distributors");
            this.finalizeSave("created", callback);
          }
        } catch (error) {
          this.loading = false;

          if (error.response && error.response.status === 404) {
            this.$store.dispatch(
              "raiseError",
              "The distributor you're trying to update no longer exists. Redirecting to distributor list."
            );
            setTimeout(() => {
              this.$router.push('/admin/distributors');
            }, 1500);
          } else {
            this.$store.dispatch(
              "raiseError",
              "An error occurred while creating the distributor. Please try again."
            );
          }
        }
      } else {
        this.loading = false;
      }
    },

    redirectToHome() {
      this.$router.push(`/admin`);
    },

    redirectToAddAccounts() {
      this.$router.push(
        `/admin/accounts/add?distributor_id=${this.distributor._jv.id}`
      );
    },

  },
};
</script>

<style scoped>
.edit-button {
  margin-bottom: 15px;
}
</style>
